// Chiffres en direct affichés dans le menu (puissance du parc, appareils en ligne, bornes en charge,
// membres). Un seul chargement partagé par tous les composants, rafraîchi toutes les minutes tant que
// l'onglet est visible ; chaque source peut échouer sans bloquer les autres.
import { useEffect, useState } from 'react';
import { wattelEMApi } from '../api/wattelEM';
import { architectureApi } from '../api/architecture';
import { getOrgBornes } from '../api/bornesService';
import { getChargingSessionStatus } from '../api/deviceService';
import { organizationsApi } from '../api/organizations';

export interface LiveStats {
  /** Puissance instantanée des WattelEM installés et en ligne (kW). */
  powerKw: number | null;
  online: number | null;
  total: number | null;
  bornes: number | null;
  charging: number | null;
  members: number | null;
}

const EMPTY: LiveStats = { powerKw: null, online: null, total: null, bornes: null, charging: null, members: null };
const REFRESH_MS = 60_000;

let stats: LiveStats = EMPTY;
let timer: ReturnType<typeof setInterval> | null = null;
let withMembers = false;
const listeners = new Set<(s: LiveStats) => void>();

const sns = (raw: string | null | undefined) => (raw || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

async function load() {
  if (document.visibilityState === 'hidden') return;
  const [ov, inst, bornes, members] = await Promise.allSettled([
    wattelEMApi.getOverview(),
    architectureApi.getInstallations().then(r => r.data),
    getOrgBornes(),
    withMembers ? organizationsApi.listMembers('current') : Promise.resolve(null),
  ]);
  const next: LiveStats = { ...stats };
  if (ov.status === 'fulfilled') {
    // même règle que l'accueil : les WattelEM rattachés à une installation, sinon tous
    const zones = inst.status === 'fulfilled' ? inst.value.flatMap(i => i.zones) : [];
    const installed = new Set(zones.flatMap(z => sns(z.wattelemSn)));
    const devices = installed.size ? ov.value.filter(d => installed.has(d.device_sn.toLowerCase())) : ov.value;
    // Un WattelEM général en ligne mesure tout son tableau : les compteurs de zone de ce tableau
    // ne sont pas ajoutés à la puissance (double comptage).
    const onlineBySn = new Map(ov.value.map(d => [d.device_sn.toLowerCase(), d.online]));
    const covered = new Set<string>();
    zones.forEach(z => {
      const g = (z.generalWattelemSn || '').trim().toLowerCase();
      if (g && onlineBySn.get(g)) sns(z.wattelemSn).forEach(s => { if (s !== g) covered.add(s); });
    });
    next.total = devices.length;
    next.online = devices.filter(d => d.online).length;
    next.powerKw = devices.filter(d => d.online && !covered.has(d.device_sn.toLowerCase())).reduce((s, d) => s + (Number(d.current?.power_kw) || 0), 0);
  }
  if (bornes.status === 'fulfilled' && bornes.value.success && bornes.value.data) {
    next.bornes = bornes.value.data.length;
    try {
      const st = await getChargingSessionStatus(bornes.value.data.map(b => b.device_sn));
      next.charging = st.success && st.data ? st.data.filter(s => s.isActive).length : null;
    } catch { next.charging = null; }
  }
  if (members.status === 'fulfilled' && Array.isArray(members.value)) next.members = members.value.length;
  stats = next;
  listeners.forEach(l => l(next));
}

/** Chiffres du menu. `includeMembers` : réservé aux org admins (liste des membres). */
export function useLiveStats(enabled: boolean, includeMembers = false): LiveStats {
  const [s, setS] = useState(stats);
  useEffect(() => {
    if (!enabled) return;
    withMembers = includeMembers;
    listeners.add(setS);
    if (!timer) {
      load();
      timer = setInterval(load, REFRESH_MS);
    }
    return () => {
      listeners.delete(setS);
      if (!listeners.size && timer) { clearInterval(timer); timer = null; }
    };
  }, [enabled, includeMembers]);
  return enabled ? s : EMPTY;
}
